import { isArchived, isInDraftStep, RequestWithRelationsFromRequestsList } from '@/models/request';
import { hasPermission, UserFromGetSession } from '@/models/user';

export const BULKABLE_TASK_TYPES = [
  'validate_transit',
  'start_article_repair',
  'start_article_exchange',
  'repair_article',
] as const;

export type BulkableTaskType = (typeof BULKABLE_TASK_TYPES)[number];

export type BulkTaskScope = {
  articleIds: string[];
  eligibleRequests: RequestWithRelationsFromRequestsList[];
};

export type BulkEligibility = {
  assign: RequestWithRelationsFromRequestsList[];
  archive: RequestWithRelationsFromRequestsList[];
  follow: {
    shouldUnfollow: boolean;
    eligible: RequestWithRelationsFromRequestsList[];
  };
  presentTaskTypes: BulkableTaskType[];
  tasks: Record<BulkableTaskType, BulkTaskScope>;
  confirmProcessingReception: BulkTaskScope;
};

export const isBulkableTaskType = (value: string | null | undefined): value is BulkableTaskType =>
  !!value && (BULKABLE_TASK_TYPES as readonly string[]).includes(value);

const canAssignSupervisor = (
  request: RequestWithRelationsFromRequestsList,
  session: UserFromGetSession | undefined | null
): boolean => {
  const workshopIds = request.allArticles
    .map((article) => article.workshopId)
    .filter((workshopId): workshopId is string => !!workshopId);

  return hasPermission(session, 'supervise_request', [
    {
      organizationId: request.organizationId,
      entityId: null,
    },
    {
      entityId: request.afterSalesId,
    },
    ...workshopIds.map((workshopId) => ({ entityId: workshopId })),
  ]);
};

const canArchiveRequest = (
  request: RequestWithRelationsFromRequestsList,
  session: UserFromGetSession | undefined | null
): boolean => {
  if (!session) {
    return false;
  }

  if (hasPermission(session, 'archive_article', { organizationId: request.organizationId })) {
    return true;
  }

  const isCreator = session.id === request.storeUserId || session.id === request.creatorId;
  const isDraftRequest = request.draft || isInDraftStep(request);

  return isCreator && isDraftRequest;
};

const isAssignDraft = (request: RequestWithRelationsFromRequestsList): boolean =>
  request.draft || isInDraftStep(request);

export type AssignSkipCounts = {
  draft: number;
  forbidden: number;
  alreadyUnassigned: number;
};

export type ArchiveSkipCounts = {
  alreadyArchived: number;
  forbidden: number;
};

export const getArchiveSkipCounts = (
  selectedRequests: RequestWithRelationsFromRequestsList[],
  eligibleRequests: RequestWithRelationsFromRequestsList[]
): ArchiveSkipCounts => {
  const eligibleIds = new Set(eligibleRequests.map((request) => request.id));
  let alreadyArchived = 0;
  let forbidden = 0;

  for (const request of selectedRequests) {
    if (isArchived(request)) {
      alreadyArchived += 1;
      continue;
    }

    if (!eligibleIds.has(request.id)) {
      forbidden += 1;
    }
  }

  return { alreadyArchived, forbidden };
};

export const getAssignSkipCounts = (
  selectedRequests: RequestWithRelationsFromRequestsList[],
  eligibleRequests: RequestWithRelationsFromRequestsList[],
  isUnassign: boolean
): AssignSkipCounts => {
  const eligibleIds = new Set(eligibleRequests.map((request) => request.id));
  let draft = 0;
  let forbidden = 0;

  for (const request of selectedRequests) {
    if (eligibleIds.has(request.id)) {
      continue;
    }

    if (isAssignDraft(request)) {
      draft += 1;
    } else {
      forbidden += 1;
    }
  }

  return {
    draft,
    forbidden,
    alreadyUnassigned: isUnassign
      ? eligibleRequests.filter((request) => !request.supervisor).length
      : 0,
  };
};

const getTaskScope = (
  requests: RequestWithRelationsFromRequestsList[],
  isEligible: (article: RequestWithRelationsFromRequestsList['articles'][number]) => boolean
): BulkTaskScope => {
  const eligibleRequests: RequestWithRelationsFromRequestsList[] = [];
  const articleIds: string[] = [];

  for (const request of requests) {
    const eligibleArticles = request.articles.filter(isEligible);

    if (eligibleArticles.length > 0) {
      eligibleRequests.push(request);
      articleIds.push(...eligibleArticles.map((article) => article.id));
    }
  }

  return { articleIds, eligibleRequests };
};

export const getBulkEligibility = (
  requests: RequestWithRelationsFromRequestsList[],
  session: UserFromGetSession | undefined | null
): BulkEligibility => {
  const assign = requests.filter(
    (request) => !isAssignDraft(request) && canAssignSupervisor(request, session)
  );
  const archive = requests.filter(
    (request) => !isArchived(request) && canArchiveRequest(request, session)
  );
  const notFollowed = requests.filter((request) => !request.hasSubscribed);
  const shouldUnfollow = requests.length > 0 && notFollowed.length === 0;
  const tasks = Object.fromEntries(
    BULKABLE_TASK_TYPES.map((taskType) => [
      taskType,
      getTaskScope(requests, (article) => article.taskType === taskType),
    ])
  ) as Record<BulkableTaskType, BulkTaskScope>;
  const presentTaskTypes = BULKABLE_TASK_TYPES.filter(
    (taskType) => tasks[taskType].articleIds.length > 0
  );

  return {
    assign,
    archive,
    follow: {
      shouldUnfollow,
      eligible: shouldUnfollow ? requests : notFollowed,
    },
    presentTaskTypes,
    tasks,
    confirmProcessingReception: getTaskScope(
      requests,
      (article) => article.canConfirmProcessingReception
    ),
  };
};
